/**
 * Oportunidades (DEMO-03, telas/Oportunidades.tsx), persistentes: UMA tela com
 * duas visões (Kanban | Lista) sobre os mesmos dados e filtros. O menu abre no
 * KANBAN (visão padrão); a Lista fica em /oportunidades/lista. A mesma Lista
 * (ListaOportunidades) é usada na aba Oportunidades do Cliente. No Kanban,
 * "Encerradas" é SÓ organização visual. Totais: cada oportunidade conta UMA
 * vez; só a versão VIGENTE da proposta entra (a Proposta chega na B3: até lá,
 * "sem proposta").
 */
import { useCallback, useState, type ReactNode } from 'react';
import { api, consulta } from '../api.ts';
import { navegar } from '../rotas.ts';
import { useSessao } from '../sessao.tsx';
import { ESTAGIOS, buscar, dataBr, estagioAberto, nomeEstagio, noPeriodo, periodoInicial, registroPrincipal, statusRegistro, textoMotivo, TIPOS, type Estagio, type FaixasRegistro, type Tipo } from '../../../backend/src/compartilhado/oportunidades.ts';
import { indicadoresOportunidades } from '../../../backend/src/compartilhado/indicadores.ts';
import { Ajuda, CampoBusca, Estado, EstadoCarga, ResumoIndicadores, SeloSituacao, Th, useCarga, useOrdenacao } from './ui.tsx';
import { ModalNovaOportunidade } from './NovaOportunidade.tsx';
import { Icone } from '../componentes/icones.tsx';
import { usePersonalizacao } from './personalizacao.tsx';
import { BarraFiltros, filtroPeriodo, filtroSelecao, FiltroSituacao, SITUACAO_PADRAO, type FiltroOpcional, SeloRegistro, SeloTipo, tomEstagio, usePeriodo, useSituacoes, type Situacao } from './filtros.tsx';
import { hojeIso } from './CampoData.tsx';
import { RegistrarPerdaSemProposta } from './proposta/NaOportunidade.tsx';
import { moeda, pct, type OpcoesOportunidade, type OportunidadeResumo } from './tiposOportunidade.ts';

/** A Oportunidade passa no filtro Situação? (qualquer estágio aberto = "Em aberto"). */
export const passaSituacao = (o: OportunidadeResumo, s: Situacao[]) => (estagioAberto(o.estagio) ? s.includes('aberta') : s.includes(o.estagio as Situacao));
const valor = (o: OportunidadeResumo) => o.valor_vigente ?? 0;
/** Parceiro selecionado OU cotado na Proposta vigente; "sem" = nenhum parceiro. */
const passaGrupo = (o: OportunidadeResumo, grupo: string) => !grupo || (grupo === 'sem' ? o.parceiros.cotados.length === 0 : o.parceiros.cotados.some((p) => p.id === grupo) || o.parceiros.selecionados.some((p) => p.id === grupo));

/** Totais por situação (F1-26: só valores; a contagem fica no rodapé da lista) — valores encerrados NUNCA são chamados de "propostas vigentes". */
export function textoTotais(l: OportunidadeResumo[]) {
  const soma = (x: OportunidadeResumo[]) => x.reduce((s, o) => s + valor(o), 0);
  const abertas = l.filter((o) => estagioAberto(o.estagio));
  const ganhas = l.filter((o) => o.estagio === 'ganha' || o.estagio === 'ganha_parcialmente');
  const semGanho = l.filter((o) => o.estagio === 'perdida' || o.estagio === 'cancelada');
  return [
    { rotulo: 'Valor nas versões vigentes', valor: moeda(soma(abertas)), mostrar: abertas.length > 0 || (ganhas.length === 0 && semGanho.length === 0) },
    { rotulo: 'Ganho', valor: moeda(soma(ganhas)), mostrar: ganhas.length > 0 },
    { rotulo: 'Encerradas sem ganho', valor: moeda(soma(semGanho)), mostrar: semGanho.length > 0 },
  ].filter((x) => x.mostrar);
}
export function LinhaTotais({ itens, teste }: { itens: OportunidadeResumo[]; teste?: string }) {
  return (
    <span className="linha-totais" data-teste={teste}>
      {textoTotais(itens).map((t, i) => (
        <span key={t.rotulo}>
          {i > 0 && <span className="sep"> · </span>}
          {t.rotulo}: <b>{t.valor}</b>
        </span>
      ))}
    </span>
  );
}

const ENCERRADOS = ESTAGIOS.filter((e) => !e.aberto).map((e) => e.id);
type DataOp = 'criacao' | 'previsao' | 'encerramento';
const DATAS_OP: { id: DataOp; rotulo: string; curto: string }[] = [
  { id: 'criacao', rotulo: 'Data de criação', curto: 'criação' },
  { id: 'previsao', rotulo: 'Previsão de fechamento', curto: 'previsão' },
  { id: 'encerramento', rotulo: 'Data do ganho/encerramento', curto: 'encerramento' },
];
const dataDaOportunidade = (o: OportunidadeResumo, qual: DataOp) => (qual === 'criacao' ? o.data_criacao : qual === 'previsao' ? o.previsao_fechamento : o.data_encerramento);

export function useFiltrosOportunidades(opcoes: OpcoesOportunidade | null) {
  const [responsavel, setResponsavel] = useState('');
  const [tipo, setTipo] = useState<'' | Tipo>('');
  const [grupo, setGrupo] = useState('');
  const { situacoes, setSituacoes } = useSituacoes();
  const per = usePeriodo<DataOp>('criacao');
  const aplicar = (l: OportunidadeResumo[], ignorarSituacao = false) =>
    l.filter((o) => (!responsavel || o.responsavel.id === responsavel) && (!tipo || o.tipo === tipo) && (ignorarSituacao || passaSituacao(o, situacoes)) && passaGrupo(o, grupo) && noPeriodo(per.periodo, dataDaOportunidade(o, per.data), hojeIso()));
  /** Critérios comuns (Situação fixa; Responsável, Tipo, Parceiro e Período em "Adicionar filtro") — a Lista acrescenta Estágio. */
  const opcionais: FiltroOpcional[] = [
    filtroSelecao('responsavel', 'Responsável', responsavel, setResponsavel, opcoes?.responsaveis ?? [], 'filtro-responsavel'),
    filtroSelecao('tipo', 'Tipo', tipo, (v) => setTipo(v as '' | Tipo), (Object.keys(TIPOS) as Tipo[]).map((t) => ({ id: t, nome: TIPOS[t] })), 'filtro-tipo'),
    filtroSelecao('parceiro', 'Parceiro comercial', grupo, setGrupo, [...(opcoes?.parceiros ?? []), { id: 'sem', nome: 'Sem parceiro definido' }], 'filtro-grupo', 'Todos os parceiros'),
    filtroPeriodo(per, DATAS_OP, 'criacao'),
  ];
  const fixos = <FiltroSituacao />;
  const limpar = () => {
    setResponsavel('');
    setTipo('');
    setGrupo('');
    per.setPeriodo(periodoInicial(hojeIso()));
    per.setData('criacao');
    setSituacoes(SITUACAO_PADRAO);
  };
  /** P2-04: estado dos filtros para as visões salvas */
  const estado = { situacoes, responsavel, tipo, grupo, periodo: per.periodo, data: per.data };
  const aplicarEstado = (e: Partial<typeof estado>) => {
    if (Array.isArray(e.situacoes)) setSituacoes(e.situacoes);
    setResponsavel(e.responsavel ?? '');
    setTipo(e.tipo ?? '');
    setGrupo(e.grupo ?? '');
    per.setPeriodo(e.periodo ?? periodoInicial(hojeIso()));
    per.setData(e.data ?? 'criacao');
  };
  const ui = (direita?: ReactNode) => <BarraFiltros teste="oportunidades" fixos={fixos} opcionais={opcionais} aoLimpar={limpar} direita={direita} />;
  /** P4 bloco 3 (I17): no Kanban, Status e Responsável ficam na mesma base da barra (os demais seguem em "Adicionar filtro"). */
  const resp = opcionais[0];
  const uiKanban = (direita?: ReactNode) => (
    <BarraFiltros
      teste="oportunidades"
      fixos={
        <>
          {fixos}
          {resp.controle}
        </>
      }
      opcionais={opcionais.slice(1)}
      aoLimpar={limpar}
      direita={direita}
    />
  );
  /** D1: os indicadores obedecem Responsável, Tipo e Parceiro; o Status recorta só a lista/Kanban. */
  const paraIndicadores = (l: OportunidadeResumo[]) => l.filter((o) => (!responsavel || o.responsavel.id === responsavel) && (!tipo || o.tipo === tipo) && passaGrupo(o, grupo));
  /** D1: o período vale para a data de criação (abertas) e a de encerramento (ganhas/perdidas), qualquer que seja a "data considerada" da lista. */
  const noPeriodoIndicador = (d: string | null | undefined) => noPeriodo(per.periodo, d ? d.slice(0, 10) : d, hojeIso());
  const comPeriodo = per.periodo.preset !== 'tudo';
  return { aplicar, ui, uiKanban, fixos, opcionais, limpar, grupo, paraIndicadores, noPeriodoIndicador, comPeriodo, estado, aplicarEstado };
}

function Alternador({ visao }: { visao: 'lista' | 'kanban' }) {
  return (
    <div className="alternador" role="group" aria-label="Visão">
      <a href="#/oportunidades" className={visao === 'kanban' ? 'ativo' : ''} aria-current={visao === 'kanban' ? 'page' : undefined}>
        Kanban
      </a>
      <a href="#/oportunidades/lista" className={visao === 'lista' ? 'ativo' : ''} aria-current={visao === 'lista' ? 'page' : undefined}>
        Lista
      </a>
    </div>
  );
}

const NOTA_TOTAIS =
  'Cada oportunidade é contada uma única vez, mesmo quando se relaciona a mais de um parceiro comercial. Só a versão vigente da proposta entra na soma (versões anteriores nunca). O filtro Parceiro comercial localiza as oportunidades em que o parceiro está selecionado ou cotado. Valores de oportunidades encerradas aparecem como ganho ou como encerradas sem ganho — nunca como propostas vigentes.';

const AJUDA_KANBAN = `Um cartão = uma oportunidade (nunca um produto). Arraste entre os estágios abertos; arrastar para “Encerradas” (ou para a faixa “Encerrar” no rodapé) abre “Registrar resultado”. A coluna Encerradas só aparece quando algum resultado fechado é escolhido em Status. Pelo teclado: abra a oportunidade e use os botões. Oportunidades abertas: soma das versões vigentes das oportunidades em aberto (Responsável, Tipo e Parceiro recortam; o Status recorta só o Kanban). ${NOTA_TOTAIS}`;

/** P2-11/P2-12 (D1): Oportunidades abertas (R$), Vendas ganhas (valor e quantidade) e conversões — Kanban e Lista. */
export function IndicadoresOportunidades({ lista, noPeriodo: np, comPeriodo }: { lista: OportunidadeResumo[]; noPeriodo: (d: string | null | undefined) => boolean; comPeriodo: boolean }) {
  const i = indicadoresOportunidades(lista, np);
  const conv = (v: number | null, em: string) => (v === null ? `— de conversão em ${em}` : `${pct(v)} de conversão em ${em}`);
  // Lixeira: ganhas cujo Pedido está na Lixeira continuam contando aqui — sinal discreto, só o "?"
  const comPedidoNaLixeira = lista.filter((o) => (o.estagio === 'ganha' || o.estagio === 'ganha_parcialmente') && o.pedido_na_lixeira && np(o.data_encerramento)).length;
  const ajudaLixeira = comPedidoNaLixeira ? (
    <Ajuda titulo="Ganhas com Pedido na Lixeira">{`${comPedidoNaLixeira} ${comPedidoNaLixeira === 1 ? 'oportunidade ganha tem' : 'oportunidades ganhas têm'} o Pedido na Lixeira e ${comPedidoNaLixeira === 1 ? 'continua' : 'continuam'} contando em "Vendas ganhas" (integral pela versão aceita; parcial pelo valor do Pedido). Os indicadores de Pedidos não contam Pedidos na Lixeira.`}</Ajuda>
  ) : undefined;
  return (
    <section className="indicadores-oportunidades" data-teste="indicadores-oportunidades" aria-label="Indicadores das Oportunidades">
      <ResumoIndicadores
        itens={[
          { rotulo: 'Oportunidades abertas', valor: moeda(i.abertas.valor), detalhe: `${i.abertas.quantidade} ${i.abertas.quantidade === 1 ? 'oportunidade' : 'oportunidades'} em aberto${comPeriodo ? ' criadas no período' : ''}`, icone: 'barras', teste: 'kpi-abertas' },
          { rotulo: 'Vendas ganhas · valor', valor: moeda(i.ganhas.valor), detalhe: conv(i.conversaoValor, 'valor'), icone: 'trofeu', teste: 'kpi-ganhas-valor', ajuda: ajudaLixeira },
          { rotulo: 'Vendas ganhas · oportunidades', valor: i.ganhas.quantidade, detalhe: conv(i.conversaoQuantidade, 'quantidade'), icone: 'alvo', teste: 'kpi-ganhas-quantidade' },
        ]}
      />
      <Ajuda titulo="Como os indicadores são calculados">
        Abertas: oportunidades em aberto criadas no período (sem período, todas as abertas), pela versão vigente. Ganhas e perdidas: pela data de encerramento no período (sem período, histórico completo); ganho parcial conta só o valor ganho. Conversão em valor = ganho ÷ (ganho + perdido); em quantidade = ganhas ÷ (ganhas + perdidas); sem ganhas nem perdidas, "—". Canceladas ficam fora. Responsável, Tipo e Parceiro recortam os indicadores; o Status recorta só a lista e o Kanban. Cada oportunidade conta uma vez.
      </Ajuda>
    </section>
  );
}

export function useDadosOportunidades(clienteId?: string, entidadeId?: string) {
  return useCarga(async () => {
    const [lista, opcoes] = await Promise.all([api.get<OportunidadeResumo[]>(`/api/oportunidades${consulta({ cliente_id: clienteId, entidade_id: entidadeId })}`), api.get<OpcoesOportunidade>('/api/oportunidades/opcoes')]);
    return { lista, opcoes };
  }, [clienteId, entidadeId]);
}

export function Oportunidades({ visao }: { visao: 'lista' | 'kanban' }) {
  const { pode, eu } = useSessao();
  const [nova, setNova] = useState(false);
  const carga = useDadosOportunidades();
  const f = useFiltrosOportunidades(carga.dados?.opcoes ?? null);
  // D7: no Kanban só visões (a Lista tem as suas, com colunas)
  const pk = usePersonalizacao(visao === 'kanban' ? 'oportunidades-kanban' : null, { filtros: f.estado, aplicarFiltros: f.aplicarEstado, semColunas: true });
  if (!carga.dados) return <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="oportunidades" />;
  const { lista, opcoes } = carga.dados;
  const base = f.aplicar(lista);
  // F1-46: sem título repetindo a trilha; linha do topo = valor (F1-26) · Kanban | Lista · Nova oportunidade (F1-50)
  // P2-11/P2-12: linha do topo = Kanban | Lista · Nova oportunidade; abaixo, os 3 indicadores (D1) no lugar de "Valor nas versões vigentes"
  const topo = () => (
    <>
      <div className="titulo-pag-linha">
        <span className="espaco" />
        <Alternador visao={visao} />
        {pode('criar', 'oportunidade') && (
          <button type="button" className="botao-primario" onClick={() => setNova(true)} data-teste="nova-oportunidade">
            <Icone nome="mais" /> Nova oportunidade
          </button>
        )}
      </div>
      <IndicadoresOportunidades lista={f.paraIndicadores(lista)} noPeriodo={f.noPeriodoIndicador} comPeriodo={f.comPeriodo} />
    </>
  );
  // P4 bloco 3 (I17): no Kanban, um único card discreto "Oportunidades abertas" à direita, acima das ações
  const abertas = indicadoresOportunidades(f.paraIndicadores(lista), f.noPeriodoIndicador).abertas;
  const topoKanban = (
    <div className="titulo-pag-linha">
      <span className="espaco" />
      <div className="card-abertas" data-teste="kpi-abertas" title="Oportunidades em aberto, pela versão vigente">
        <span className="rotulo">Oportunidades abertas</span>
        <strong data-teste="kpi-abertas-valor">{moeda(abertas.valor)}</strong>
        <span className="detalhe">
          {abertas.quantidade} {abertas.quantidade === 1 ? 'oportunidade' : 'oportunidades'}
          {f.comPeriodo ? ' criadas no período' : ''}
        </span>
      </div>
      <Alternador visao={visao} />
      {pode('criar', 'oportunidade') && (
        <button type="button" className="botao-primario" onClick={() => setNova(true)} data-teste="nova-oportunidade">
          <Icone nome="mais" /> Nova oportunidade
        </button>
      )}
    </div>
  );
  return (
    <>
      {visao === 'lista' ? (
        <ListaOportunidades base={base} baseBusca={f.aplicar(lista, true)} opcoes={opcoes} filtros={f} topo={topo} />
      ) : (
        <>
          {topoKanban}
          {f.uiKanban(
            <>
              {pk.controles}
              <Ajuda titulo="Como usar o Kanban">{AJUDA_KANBAN}</Ajuda>
            </>,
          )}
          <Kanban base={base} opcoes={opcoes} aoMudar={() => void carga.recarregar()} />
        </>
      )}
      {nova && <ModalNovaOportunidade responsaveis={opcoes.responsaveis} responsavelPadrao={eu?.id} aoFechar={() => setNova(false)} />}
    </>
  );
}

type ColOp = 'oportunidade' | 'tipo' | 'estagio' | 'valor' | 'margem' | 'margemPct' | 'parceiro' | 'registro' | 'proxima' | 'previsao' | 'responsavel' | 'situacao' | 'unidade';

/**
 * Lista de Oportunidades — COMPONENTE ÚNICO usado na Lista geral e na aba
 * Oportunidades do Cliente (com a coluna Cliente oculta), para as duas visões nunca divergirem.
 */
export function ListaOportunidades({ base, baseBusca, opcoes, filtros, ocultarCliente, teste = 'lista-oportunidades', totalTeste = 'total-lista', topo, filial, aoLimparFilial, faixa, lista: idLista = 'oportunidades-lista' }: { base: OportunidadeResumo[]; baseBusca?: OportunidadeResumo[]; opcoes: OpcoesOportunidade; filtros?: Pick<ReturnType<typeof useFiltrosOportunidades>, 'fixos' | 'opcionais' | 'limpar' | 'estado' | 'aplicarEstado'>; /** P2-03/P2-04: lista para colunas e visões */ lista?: 'oportunidades-lista' | 'cliente-oportunidades'; ocultarCliente?: boolean; teste?: string; totalTeste?: string; topo?: (lista: OportunidadeResumo[]) => ReactNode; /** P2-18: Filial/unidade (aba do Cliente) logo após a busca */ filial?: ReactNode; aoLimparFilial?: () => void; /** P2-19: faixa entre os filtros e a tabela */ faixa?: (lista: OportunidadeResumo[]) => ReactNode }) {
  const [estagio, setEstagio] = useState<'' | 'abertas' | Estagio>('');
  const ord = useOrdenacao<ColOp>('oportunidade', 'desc');
  const [busca, setBusca] = useState('');
  const pers = usePersonalizacao(idLista, { busca: { valor: busca, definir: setBusca }, filtros: { ...(filtros?.estado ?? {}), estagio }, aplicarFiltros: (e: Record<string, unknown>) => (filtros?.aplicarEstado(e), setEstagio((e.estagio as '' | 'abertas' | Estagio) ?? '')), ordenacao: ord });
  const { situacoes } = useSituacoes();
  const hoje = opcoes.hoje;
  // A busca por identificador considera TODAS as situações; os demais filtros continuam valendo.
  const achados = buscar(baseBusca ?? base, (o) => o.chaves, busca);
  const motivo = new Map((achados ?? []).map((r) => [r.item.id, textoMotivo(r, 'oportunidade')]));
  const conta = (e: Estagio) => base.filter((o) => o.estagio === e).length;
  const filtrada = (achados ? achados.map((r) => r.item) : base).filter((o) => !estagio || (estagio === 'abertas' ? estagioAberto(o.estagio) : o.estagio === estagio));
  const lista = ord.ordenar(
    filtrada,
    {
      oportunidade: (o) => Number(o.numero),
      unidade: (o) => (o.entidade ? o.entidade.razao_social : null),
      tipo: (o) => TIPOS[o.tipo],
      estagio: (o) => ESTAGIOS.findIndex((e) => e.id === o.estagio),
      valor: (o) => (o.proposta ? valor(o) : null),
      margem: (o) => o.margem?.bruta,
      margemPct: (o) => o.margem?.percentual,
      parceiro: (o) => o.parceiros.selecionados.map((x) => x.nome).join(', ') || o.parceiros.cotados.map((x) => x.nome).join(', '),
      registro: (o) => registroPrincipal(o.registros)?.data_vencimento,
      proxima: (o) => o.proxima_acao?.data,
      previsao: (o) => o.previsao_fechamento,
      responsavel: (o) => o.responsavel.nome,
      situacao: (o) => (estagioAberto(o.estagio) ? '0' : `1${o.data_encerramento ?? ''}`),
    },
    (a, b) => Number(b.numero) - Number(a.numero),
  );
  const filtroEstagio: FiltroOpcional = {
    id: 'estagio',
    rotulo: 'Estágio',
    ativo: estagio !== '',
    resumo: estagio === 'abertas' ? 'Somente abertas' : estagio ? nomeEstagio(estagio) : '',
    controle: (
      <label>
        Estágio
        <select value={estagio} onChange={(e) => setEstagio(e.target.value as '' | 'abertas' | Estagio)} data-teste="filtro-estagio">
          <option value="">Todos ({base.length})</option>
          <option value="abertas">Somente abertas</option>
          {ESTAGIOS.map((e) => (
            <option key={e.id} value={e.id}>
              {e.nome} ({conta(e.id)})
            </option>
          ))}
        </select>
      </label>
    ),
    limpar: () => setEstagio(''),
  };
  return (
    <>
      {topo ? topo(lista) : <LinhaTotais itens={lista} teste={totalTeste} />}
      <BarraFiltros
        teste="oportunidades"
        fixos={
          <>
            <CampoBusca valor={busca} setValor={setBusca} rotulo="Buscar oportunidade" ajuda="Nº, PED-, OCF-, cliente ou CNPJ" dica="Nº da Oportunidade, Proposta, PED-, OCF-, OL-, OTI-, OC do Cliente, Cliente ou CNPJ" teste="busca-oportunidades" total={achados ? lista.length : null} />
            {filial}
            {filtros?.fixos}
          </>
        }
        opcionais={[...(filtros?.opcionais ?? []), filtroEstagio]}
        direita={pers.controles}
        aoLimpar={() => {
          filtros?.limpar();
          setEstagio('');
          setBusca('');
          aoLimparFilial?.();
        }}
      />
      {faixa?.(lista)}
      {busca.trim() && baseBusca && (
        <p className="texto-secundario sem-margem" data-teste="busca-todas-situacoes">
          A busca considera todas as situações (inclusive encerradas); os demais filtros continuam valendo.
        </p>
      )}
      {situacoes.length === 0 ? (
        <Estado tipo="vazio" titulo="nenhum status selecionado — use Selecionar todas ou escolha ao menos um" teste={`${teste}-vazia`} />
      ) : lista.length === 0 ? (
        <p className="vazio" data-teste={`${teste}-vazia`}>
          Nenhuma oportunidade {ocultarCliente ? 'deste cliente ' : ''}com {busca.trim() ? 'a busca e ' : ''}os filtros atuais.
        </p>
      ) : (
        <div className="rolagem primeira-fixa">
          <table ref={pers.tabela} className="tabela tabela-oportunidades" data-teste={teste}>
            <thead>
              <tr>
                <Th ord={ord} coluna="oportunidade" padrao="desc" className="col-op">Oportunidade</Th>
                {/* P3-10: no Cliente 360º, a unidade real da Oportunidade logo após a identificação */}
                {ocultarCliente && <Th ord={ord} coluna="unidade">Filial/unidade</Th>}
                <Th ord={ord} coluna="tipo">Tipo</Th>
                <Th ord={ord} coluna="estagio">Estágio</Th>
                <Th ord={ord} coluna="valor" padrao="desc" numero>Valor vigente</Th>
                <Th ord={ord} coluna="margem" padrao="desc" numero>Margem (R$)</Th>
                <Th ord={ord} coluna="margemPct" padrao="desc" numero>Margem (%)</Th>
                <Th ord={ord} coluna="parceiro">Parceiro comercial</Th>
                <Th ord={ord} coluna="registro">Registro</Th>
                <Th ord={ord} coluna="proxima">Próxima ação</Th>
                <Th ord={ord} coluna="previsao">Previsão</Th>
                <Th ord={ord} coluna="responsavel">Responsável</Th>
                <Th ord={ord} coluna="situacao">Status</Th>
              </tr>
            </thead>
            <tbody>
              {lista.map((o) => {
                const r = registroPrincipal(o.registros);
                const vencida = o.proxima_acao && o.proxima_acao.data < hoje && estagioAberto(o.estagio);
                const cotadosSo = o.parceiros.cotados.filter((x) => !o.parceiros.selecionados.some((s) => s.id === x.id));
                return (
                  <tr key={o.id} data-teste={`linha-op-${o.numero}`}>
                    <td>
                      <a href={`#/oportunidades/${o.id}`} className="titulo-op">
                        {o.titulo}
                      </a>
                      <div className="texto-secundario">
                        #{o.numero}
                        {!ocultarCliente && ` · ${o.cliente.nome}`}
                      </div>
                      {motivo.get(o.id) && (
                        <span className="motivo-busca" data-teste={`motivo-${o.numero}`}>
                          {motivo.get(o.id)}
                        </span>
                      )}
                    </td>
                    {ocultarCliente && (
                      <td data-teste={`unidade-op-${o.numero}`}>
                        {o.entidade ? (
                          <>
                            {o.entidade.razao_social}
                            <div className="texto-secundario nowrap">{o.entidade.cnpj}</div>
                          </>
                        ) : (
                          <span className="ausente">Não informada</span>
                        )}
                      </td>
                    )}
                    <td>
                      <SeloTipo tipo={o.tipo} />
                    </td>
                    <td>
                      <SeloSituacao texto={nomeEstagio(o.estagio)} tom={tomEstagio(o.estagio)} />
                    </td>
                    <td className="nowrap numero">{o.proposta ? moeda(valor(o)) : <span className="ausente">sem proposta</span>}</td>
                    <td className="nowrap numero" data-teste={`margem-op-${o.numero}`}>
                      {o.margem ? moeda(o.margem.bruta) : <span className="ausente">—</span>}
                    </td>
                    <td className="nowrap numero" data-teste={`margem-pct-op-${o.numero}`}>
                      {o.margem ? pct(o.margem.percentual) : <span className="ausente">—</span>}
                    </td>
                    <td>
                      {o.parceiros.cotados.length ? (
                        <>
                          {o.parceiros.selecionados.map((x) => x.nome).join(', ') || <span className="texto-secundario">nenhum selecionado</span>}
                          {cotadosSo.length > 0 && <div className="texto-secundario">cotado: {cotadosSo.map((x) => x.nome).join(', ')}</div>}
                        </>
                      ) : (
                        <span className="texto-secundario">Sem parceiro definido</span>
                      )}
                    </td>
                    <td>
                      {r && opcoes.faixas_registro ? (
                        <>
                          <div className="nowrap">
                            {statusRegistro(r, hoje, opcoes.faixas_registro).dias < 0 ? 'venceu em' : 'vence'} {dataBr(r.data_vencimento)}
                          </div>
                          <SeloRegistro r={r} faixas={opcoes.faixas_registro} hoje={hoje} curto vencimento="oculto" />
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      {o.proxima_acao ? (
                        <>
                          {o.proxima_acao.descricao}
                          <div className={vencida ? 'alerta-acao' : 'texto-secundario'}>{vencida ? `✖ venceu em ${dataBr(o.proxima_acao.data)}` : dataBr(o.proxima_acao.data)}</div>
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="nowrap">{o.previsao_fechamento ? dataBr(o.previsao_fechamento) : '—'}</td>
                    <td>{o.responsavel.nome}</td>
                    <td>{estagioAberto(o.estagio) ? 'Em aberto' : `Encerrada${o.data_encerramento ? ` em ${dataBr(o.data_encerramento)}` : ''}`}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="rodape-tabela" data-teste={`${teste}-rodape`}>
            {lista.length} oportunidade(s)
            <Ajuda titulo="Como os totais são somados">{NOTA_TOTAIS}</Ajuda>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Cartão do Kanban — componente ESTÁVEL (fora do Kanban). C20: definido dentro do Kanban, era recriado a cada
 * renderização e o início do arraste (que renderiza de novo) tirava o próprio cartão do DOM, perdendo a origem.
 */
function CartaoKanban({ o, hoje, faixas, podeMover, aoIniciar, aoTerminar }: { o: OportunidadeResumo; hoje: string; faixas: OpcoesOportunidade['faixas_registro']; podeMover: boolean; aoIniciar: (id: string) => void; aoTerminar: () => void }) {
  const vencida = o.proxima_acao && o.proxima_acao.data < hoje;
  const r = registroPrincipal(o.registros);
  return (
    <li
      className="cartao-kanban"
      draggable={podeMover && estagioAberto(o.estagio)}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', o.id);
        e.dataTransfer.effectAllowed = 'move';
        aoIniciar(o.id);
      }}
      onDragEnd={aoTerminar}
      data-teste={`cartao-${o.numero}`}
    >
      {/* P4 bloco 3 (I17): número e Cliente juntos; tipo e valor na mesma linha; responsável e próxima ação abaixo */}
      <a href={`#/oportunidades/${o.id}`} draggable={false}>
        <span className="linha-id">
          <span className="num-op">#{o.numero}</span>
          <strong className="cli-op">{o.cliente.nome}</strong>
        </span>
        <span className="tit-op">{o.titulo}</span>
      </a>
      <div className="cartao-selos">
        <SeloTipo tipo={o.tipo} />
        {!estagioAberto(o.estagio) && <SeloSituacao texto={nomeEstagio(o.estagio)} tom={tomEstagio(o.estagio)} />}
        <span className="valor-op">{o.proposta ? moeda(valor(o)) : 'sem proposta'}</span>
      </div>
      {r && faixas && <SeloRegistro r={r} faixas={faixas} hoje={hoje} curto vencimento="data" />}
      <span className="texto-secundario meta-op">
        {o.responsavel.nome}
        {o.proxima_acao && (vencida ? <span className="alerta-acao"> · ⚠ próxima ação vencida</span> : ` · Próxima ação: ${dataBr(o.proxima_acao.data)}`)}
      </span>
    </li>
  );
}

function Kanban({ base, opcoes, aoMudar }: { base: OportunidadeResumo[]; opcoes: OpcoesOportunidade; aoMudar: () => void }) {
  const { pode } = useSessao();
  const { situacoes } = useSituacoes();
  const [arrastando, setArrastando] = useState<string | null>(null);
  const [sobre, setSobre] = useState<string | null>(null);
  const [resultado, setResultado] = useState<OportunidadeResumo | null>(null);
  const [erro, setErro] = useState('');
  const mostrarAbertas = situacoes.includes('aberta');
  const fechadasSelecionadas = ENCERRADOS.filter((e) => situacoes.includes(e as Situacao));
  const colunas = ESTAGIOS.filter((e) => e.aberto);
  const hoje = opcoes.hoje;
  const podeMover = pode('editar', 'oportunidade');

  const soltar = async (destino: Estagio | 'encerradas', id: string | null) => {
    const o = base.find((x) => x.id === (id || arrastando));
    setArrastando(null);
    setSobre(null);
    setErro('');
    if (!o || !estagioAberto(o.estagio)) return;
    // Fechamento único (09/10/2026): com proposta enviada, o resultado é registrado na Proposta (versão vigente)
    if (destino === 'encerradas') return o.proposta && o.proposta.situacao !== 'rascunho' ? navegar(`/oportunidades/${o.id}/propostas/${o.proposta.id}/v/${o.proposta.versao}`) : setResultado(o);
    if (destino === o.estagio) return;
    try {
      await api.post(`/api/oportunidades/${o.id}/estagio`, { estagio: destino });
      aoMudar();
    } catch (e) {
      setErro((e as Error).message);
    }
  };

  const terminar = useCallback(() => (setArrastando(null), setSobre(null)), []);
  const cartao = (o: OportunidadeResumo) => <CartaoKanban key={o.id} o={o} hoje={hoje} faixas={opcoes.faixas_registro} podeMover={podeMover} aoIniciar={setArrastando} aoTerminar={terminar} />;
  /** Destino do arraste: destaca a coluna sob o cartão (só visual; nada muda de lugar). */
  const alvo = (id: string, destino: Estagio | 'encerradas') => ({
    onDragOver: (e: { preventDefault: () => void }) => (e.preventDefault(), sobre !== id && setSobre(id)),
    onDragLeave: (e: { currentTarget: Element; relatedTarget: EventTarget | null }) => !e.currentTarget.contains(e.relatedTarget as Node | null) && setSobre((x) => (x === id ? null : x)),
    onDrop: (e: { preventDefault: () => void; dataTransfer: DataTransfer }) => (e.preventDefault(), void soltar(destino, e.dataTransfer.getData('text/plain'))),
  });
  /** F1-24: cabeçalho da coluna = nome, quantidade e valor (sem frases). */
  const Soma = ({ itens, encerradas }: { itens: OportunidadeResumo[]; encerradas?: boolean }): ReactNode => (
    <p className="soma-coluna" data-teste="soma-coluna">
      {encerradas ? textoTotais(itens).map((t) => `${t.rotulo}: ${t.valor}`).join(' · ') : moeda(itens.reduce((s, o) => s + valor(o), 0))}
    </p>
  );

  const encerradas = base.filter((o) => !estagioAberto(o.estagio));
  if (situacoes.length === 0) return <Estado tipo="vazio" titulo="nenhum status selecionado — use Selecionar todas ou escolha ao menos um" teste="kanban-vazio" />;
  return (
    <>
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      <div className="kanban" data-teste="kanban">
        {mostrarAbertas &&
          colunas.map((c) => {
            const itens = base.filter((o) => o.estagio === c.id);
            return (
              <section key={c.id} className={`coluna-kanban cab-estagio estagio-${c.id}${sobre === c.id ? ' alvo-arraste' : ''}`} aria-label={`${c.nome}: ${itens.length}`} {...alvo(c.id, c.id)} data-teste={`coluna-${c.id}`}>
                <h2>
                  {c.nome} <span className="contador">{itens.length}</span>
                  <span className="soma-coluna" data-teste="soma-coluna">
                    {moeda(itens.reduce((s2, o) => s2 + valor(o), 0))}
                  </span>
                </h2>
                <ul>
                  {itens.map(cartao)}
                </ul>
              </section>
            );
          })}
        {fechadasSelecionadas.length > 0 && (
          <section className={`coluna-kanban coluna-encerradas${sobre === 'encerradas' ? ' alvo-arraste' : ''}`} aria-label={`Encerradas: ${encerradas.length}`} {...alvo('encerradas', 'encerradas')} data-teste="coluna-encerradas">
            <h2>
              Encerradas <span className="contador">{encerradas.length}</span>
            </h2>
            <p className="texto-secundario sem-margem" data-teste="encerradas-filtro">
              Só: {fechadasSelecionadas.map((e) => nomeEstagio(e)).join(' + ')}
            </p>
            <Soma itens={encerradas} encerradas />
            <ul>
              {encerradas.map(cartao)}
            </ul>
          </section>
        )}
      </div>
      {/* C20: a zona "Encerrar" é uma faixa FIXA no rodapé — aparece sem deslocar nem estreitar as colunas. */}
      {arrastando && fechadasSelecionadas.length === 0 && (
        <section className={`zona-encerrar-fixa${sobre === 'zona' ? ' alvo-arraste' : ''}`} aria-label="Encerrar: registrar resultado" {...alvo('zona', 'encerradas')} data-teste="zona-encerrar">
          Solte aqui para encerrar e registrar o resultado
        </section>
      )}
      {resultado && <RegistrarPerdaSemProposta oportunidade={resultado} motivos={opcoes.motivos_perda} aoFechar={() => setResultado(null)} aoSalvar={() => (setResultado(null), aoMudar())} />}
    </>
  );
}

export type { FaixasRegistro };
